import { useEffect, useRef, useState } from 'react';
import { isMaskedSsn } from '@ehr/shared';
import { api, apiErrorText } from '../lib/api.js';
import { maskSsn } from '../lib/contactFields.js';

/**
 * ehr#712 (Jay 10-30): the Demographics SSN input. A saved SSN comes from the server masked
 * (***-**-1234) and shows read only, with an Edit button. Edit fetches the full number through the
 * audited reveal route so a transposed digit can be fixed in place; Cancel puts the mask back, and
 * a save with the mask untouched keeps the stored SSN (the server swaps it back). If the reveal is
 * refused the field opens empty to retype the whole number.
 */
export function SsnField(props: {
  contactId: string;
  /** The Demographics section the field lives in (identity, guarantor, guarantor#2...). */
  section: string;
  value: string;
  /** A change the user typed (marks the record as having unsaved changes). */
  onChange: (v: string) => void;
  /** Edit's revealed value, or Cancel's mask: shown, but not an unsaved change on its own. */
  onRestore: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
  invalid?: boolean;
  label: string;
}) {
  const { contactId, section, value, onChange, onRestore } = props;
  const masked = isMaskedSsn(value);
  // The mask the user pressed Edit on, for Cancel; null while no edit of a saved SSN is open.
  const [editingFrom, setEditingFrom] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  // The reveal failed: an empty input to retype into, while the record keeps the mask (so saving
  // without typing still keeps the stored SSN).
  const [retype, setRetype] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Masked again (the save re-masked it, or a reload): no edit is open any more.
  useEffect(() => {
    if (!masked) return;
    setEditingFrom(null);
    setRetype(false);
  }, [masked]);

  const startEdit = () => {
    if (busy) return;
    const from = value;
    setBusy(true);
    setNote('');
    api
      .get<{ ssn: string }>(
        `/contacts/${contactId}/demographics/ssn?section=${encodeURIComponent(section)}`,
      )
      .then((r) => {
        setEditingFrom(from);
        onRestore(maskSsn(r.ssn ?? ''));
      })
      .catch((e: unknown) => {
        // Can't show the stored number: retype it in full instead.
        setEditingFrom(from);
        setRetype(true);
        const why = apiErrorText(e, 'Could not show the saved SSN').replace(/\.?$/, '.');
        setNote(`${why} Type the full SSN to replace it.`);
      })
      .finally(() => {
        setBusy(false);
        setTimeout(() => inputRef.current?.focus(), 0);
      });
  };

  const cancelEdit = () => {
    if (editingFrom != null) onRestore(editingFrom);
    setEditingFrom(null);
    setRetype(false);
    setNote('');
  };

  if (masked && !retype) {
    return (
      <div className="ssn-row">
        <input
          type="text"
          className="ssn-masked"
          value={value}
          readOnly
          aria-label={`${props.label}, saved, showing last 4 digits`}
          aria-readonly="true"
        />
        <button
          type="button"
          className="btn sm ssn-edit-btn"
          onClick={startEdit}
          disabled={busy}
          aria-label={`Edit ${props.label}`}
        >
          {busy ? 'Opening…' : 'Edit'}
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="ssn-row">
        <input
          ref={inputRef}
          type="text"
          className={props.invalid ? 'demo-input-invalid' : undefined}
          value={masked ? '' : value}
          placeholder={props.placeholder}
          inputMode="numeric"
          autoComplete="off"
          maxLength={props.maxLength}
          aria-label={props.label}
          onChange={(e) => onChange(maskSsn(e.target.value))}
        />
        {editingFrom != null && (
          <button type="button" className="btn sm ssn-edit-btn" onClick={cancelEdit}>
            Cancel
          </button>
        )}
      </div>
      {note && (
        <span className="demo-field-err" role="status">
          {note}
        </span>
      )}
    </>
  );
}
